import React, { useCallback, useMemo } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Button,
  Paper,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Typography
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material'
import { apiClient } from '../../services/api'
import { useDataFetching } from '../../hooks/useDataFetching'
import { useListPageState } from '../../hooks/useListPageState'
import AdminPageHeader from '../../components/admin/shared/AdminPageHeader'
import BaseDataTable, { TableColumn } from '../../components/shared/BaseDataTable'
import { poolwerxColors } from '../../theme/colors'
import { getMarketChipColor } from '../../utils/marketChip'

interface BDM {
  id: number
  first_name: string
  last_name: string
  email: string
  phone?: string
  market: string
  notes?: string
  is_active: boolean
  full_name: string
  active_fp_count: number
  default_for_regions?: string[]
  created_at: string
  updated_at: string
}

interface BDMListResponse {
  bdms: BDM[]
  total: number
  page: number
  per_page: number
  pages: number
}

const BDMList: React.FC = () => {
  const { state, updateField, updateFields } = useListPageState({
    defaultSortField: 'full_name',
    defaultPerPage: 100
  })

  const fetchBdms = useCallback(async () => {
    const response = await apiClient.get('/admin/bdm?per_page=100')
    return response.data as BDMListResponse
  }, [])

  const { data, loading, error, refetch } = useDataFetching<BDMListResponse>({
    fetchFn: fetchBdms,
    dependencies: [],
    immediate: true
  })

  const filteredBdms = useMemo(() => {
    let filtered = data?.bdms ?? []

    const searchText = (state.search as string)?.toLowerCase()
    if (searchText) {
      filtered = filtered.filter(bdm =>
        bdm.full_name.toLowerCase().includes(searchText) ||
        bdm.email.toLowerCase().includes(searchText)
      )
    }

    if (state.market) {
      filtered = filtered.filter(bdm => bdm.market === state.market)
    }

    if (state.activeFilter === 'true' || state.activeFilter === 'false') {
      const wantActive = state.activeFilter === 'true'
      filtered = filtered.filter(bdm => bdm.is_active === wantActive)
    }

    const { sortField, sortDirection } = state
    return [...filtered].sort((a, b) => {
      const aVal = (a as any)[sortField] ?? ''
      const bVal = (b as any)[sortField] ?? ''
      const cmp = typeof aVal === 'string'
        ? aVal.localeCompare(bVal)
        : aVal < bVal ? -1 : aVal > bVal ? 1 : 0
      return sortDirection === 'desc' ? -cmp : cmp
    })
  }, [data, state.search, state.market, state.activeFilter, state.sortField, state.sortDirection])

  const columns: TableColumn<BDM>[] = [
    {
      key: 'full_name',
      label: 'Name',
      sortable: true,
      render: (bdm) => (
        <Typography variant="body2" fontWeight="medium">
          {bdm.full_name}
        </Typography>
      )
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      render: (bdm) => (
        <Typography variant="body2" color="textSecondary">
          {bdm.email}
        </Typography>
      )
    },
    {
      key: 'phone',
      label: 'Phone',
      render: (bdm) => (
        <Typography variant="body2">{bdm.phone || '-'}</Typography>
      )
    },
    {
      key: 'market',
      label: 'Market',
      sortable: true,
      render: (bdm) => (
        <Chip label={bdm.market} size="small" color={getMarketChipColor(bdm.market)} />
      )
    },
    {
      key: 'default_for_regions',
      label: 'Default For',
      render: (bdm) => (
        bdm.default_for_regions && bdm.default_for_regions.length > 0 ? (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {bdm.default_for_regions.map((region, idx) => (
              <Chip key={idx} label={region} size="small" variant="outlined" />
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="textSecondary">-</Typography>
        )
      )
    },
    {
      key: 'active_fp_count',
      label: 'Active FPs',
      align: 'center',
      sortable: true,
      render: (bdm) => (
        <Chip
          label={bdm.active_fp_count}
          size="small"
          color={bdm.active_fp_count > 0 ? 'info' : 'default'}
        />
      )
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      render: (bdm) => (
        <Chip
          label={bdm.is_active ? 'Active' : 'Inactive'}
          size="small"
          color={bdm.is_active ? 'success' : 'default'}
        />
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      render: (bdm) => (
        <IconButton
          component={RouterLink}
          to={`/admin/system/bdm/${bdm.id}/edit`}
          size="small"
          title="Edit"
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )
    }
  ]

  return (
    <>
      <AdminPageHeader
        pageKey="bdm"
        actions={
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refetch}>
              Refresh
            </Button>
            <Button
              component={RouterLink}
              to="/admin/system/bdm/new"
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                backgroundColor: poolwerxColors.teal,
                '&:hover': { backgroundColor: poolwerxColors.tealHover }
              }}
            >
              Add ROM/FBC
            </Button>
          </Stack>
        }
      />

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            size="small"
            placeholder="Search by name or email..."
            value={state.search}
            onChange={(e) => updateField('search', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Market</InputLabel>
            <Select
              value={state.market || ''}
              label="Market"
              onChange={(e) => updateField('market', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="ANZ">ANZ</MenuItem>
              <MenuItem value="USA">USA</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={state.activeFilter || ''}
              label="Status"
              onChange={(e) => updateField('activeFilter', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      <BaseDataTable
        variant="enhanced"
        columns={columns}
        data={filteredBdms}
        loading={loading}
        error={error}
        sortField={state.sortField}
        sortDirection={state.sortDirection}
        onSort={(field, direction) => updateFields({ sortField: field, sortDirection: direction })}
        showCount
        countLabel="ROM/FBCs"
        emptyMessage="No ROM/FBC records found"
        getRowKey={(bdm) => bdm.id}
      />
    </>
  )
}

export default BDMList
